import React, { useEffect, useRef, useState } from 'react';
import { apiClient } from '../../api/client';
import { Button } from '../ui/button';
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const Section = ({ title, accent, children }) => {
    const dot = accent === 'green' ? 'bg-emerald-400' : accent === 'red' ? 'bg-rose-500' : 'bg-amber-400';
    return (
        <div className="py-2.5 border-t border-slate-700/50 first:border-t-0">
            <div className="flex items-center gap-2 mb-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
                <span className="text-[10px] uppercase tracking-[0.1em] text-slate-300 font-medium">{title}</span>
            </div>
            <div className="text-xs text-slate-200 leading-relaxed pl-3.5">{children}</div>
        </div>
    );
};

export default function DecisionReport({ symbol, timeframe }) {
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const current = useRef(symbol);

    useEffect(() => {
        current.current = symbol;
        setReport(null);
        setLoading(false);
    }, [symbol]);

    const generate = async () => {
        if (!symbol) {
            toast.error('Önce bir coin seç');
            return;
        }
        setLoading(true);
        setReport(null);
        try {
            const asked = symbol;
            const { data } = await apiClient.post('/reports/generate', { symbol, timeframe });
            if (current.current !== asked) return;
            setReport({ ...data.report, _symbol: asked });
            toast.success(`${asked.replace('USDT', '')} raporu hazır`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Rapor oluşturulamadı');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="border border-slate-700 bg-slate-800 rounded-md overflow-hidden h-full flex flex-col" data-testid="decision-report-card">
            <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold tracking-tight text-slate-100">Karar Destek Raporu</h3>
                    <p className="text-[11px] text-slate-400 mt-0.5">
                        Türkçe · {timeframe === 'short' ? 'Çok Kısa Vade (15m–1h)' : 'Kısa Vade (4h–1d)'}
                    </p>
                </div>
                <Button onClick={generate} disabled={loading || !symbol} data-testid="generate-report-button" size="sm" className="h-8 rounded-sm bg-emerald-500 text-white hover:bg-emerald-400 text-[11px] uppercase tracking-wider font-medium">
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Rapor Üret</>}
                </Button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 text-sm">
                {!report && !loading && (
                    <div className="text-slate-400 text-xs leading-relaxed">
                        Seçili coin için teknik, türev ve haber sentiment verilerini birleştiren
                        kontrollü dilli Türkçe rapor üretilir. Sistem yatırım tavsiyesi vermez;
                        yalnızca olasılık, risk ve senaryo çerçevesinde yorum üretir.
                    </div>
                )}

                {loading && (
                    <div className="flex items-center gap-2 text-slate-400 text-xs">
                        <Loader2 className="h-4 w-4 animate-spin" /> Claude rapor üretiyor...
                    </div>
                )}

                {report && (
                    <div className="space-y-1" data-testid="report-content">
                        <div className="text-[10px] uppercase tracking-wider text-slate-400" data-testid="report-symbol">{report._symbol?.replace('USDT', '')} için rapor</div>
                        <div className="text-sm text-slate-100 leading-relaxed mb-2" data-testid="report-summary">{report.ozet}</div>

                        <Section title="Yükseliş Senaryosu" accent="green">
                            <p className="text-slate-200">{report.yukselis_senaryosu?.yorum}</p>
                            {report.yukselis_senaryosu?.tetikleyiciler?.length > 0 && (
                                <ul className="mt-1 space-y-0.5">
                                    {report.yukselis_senaryosu.tetikleyiciler.map((t) => (
                                        <li key={`bull-${t}`} className="text-slate-300 text-[11px] before:content-['▸'] before:text-emerald-400 before:mr-1.5">{t}</li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Düşüş Senaryosu" accent="red">
                            <p className="text-slate-200">{report.dusus_senaryosu?.yorum}</p>
                            {report.dusus_senaryosu?.tetikleyiciler?.length > 0 && (
                                <ul className="mt-1 space-y-0.5">
                                    {report.dusus_senaryosu.tetikleyiciler.map((t) => (
                                        <li key={`bear-${t}`} className="text-slate-300 text-[11px] before:content-['▸'] before:text-rose-400 before:mr-1.5">{t}</li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Nötr / Bekle-Gör" accent="yellow">
                            <p className="text-slate-200">{report.notr_senaryo?.yorum}</p>
                        </Section>

                        {report.dikkat_metrikleri?.length > 0 && (
                            <Section title="Dikkat Metrikleri" accent="yellow">
                                <ul className="space-y-0.5">
                                    {report.dikkat_metrikleri.map((t) => (
                                        <li key={`dm-${t}`} className="text-slate-300 text-[11px] before:content-['•'] before:text-slate-400 before:mr-1.5">{t}</li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        {report.gecersizlik_alanlari?.length > 0 && (
                            <Section title="Geçersizlik Alanları" accent="red">
                                <ul className="space-y-0.5">
                                    {report.gecersizlik_alanlari.map((t) => (
                                        <li key={`ga-${t}`} className="text-slate-300 text-[11px] before:content-['×'] before:text-rose-500 before:mr-1.5">{t}</li>
                                    ))}
                                </ul>
                            </Section>
                        )}

                        <div className="mt-3 pt-3 border-t border-slate-700/50 flex items-start gap-2 text-[10px] text-slate-400 leading-relaxed">
                            <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                            <span>{report.uyari || 'Yatırım tavsiyesi değildir.'} <span className="text-slate-500">({report._source})</span></span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
